import { useState, useEffect, useCallback } from 'react';
import axios from '../utility/axios';
import { useTableChanges } from './useWebSocket';

export const useUserSettings = () => {
    const [userSettings, setUserSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchUserSettings = useCallback(async (silent = false) => {
        try {
            if (!silent) {
                setLoading(true);
            }
            const response = await axios.get('/api/user-settings/');
            
            if (response.data.success) {
                setUserSettings(response.data.settings);
            } else {
                setError(response.data.error || 'Failed to load user settings');
            }
        } catch (err) {
            console.error('Error fetching user settings:', err);
            setError(err.response?.data?.error || 'Failed to load user settings');
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, []);

    // Handle real-time user_settings changes
    const handleSettingsChange = useCallback((change) => {
        if (change.operation === 'UPDATE' || change.operation === 'INSERT') {
            // Settings changed - refresh silently
            fetchUserSettings(true);
        }
    }, [fetchUserSettings]);

    // Subscribe to real-time user_settings table changes
    const { isConnected } = useTableChanges('user_settings', handleSettingsChange);

    useEffect(() => {
        fetchUserSettings();
    }, [fetchUserSettings]);

    const updateUserSettings = async (updatedSettings) => {
        try {
            const response = await axios.post('/api/user-settings/update/', updatedSettings);
            
            if (response.data.success) {
                setUserSettings(response.data.settings);
                return { success: true };
            } else {
                return { success: false, error: response.data.error };
            }
        } catch (err) {
            console.error('Error updating user settings:', err);
            return { success: false, error: err.response?.data?.error || 'Failed to update settings' };
        }
    };

    return { 
        userSettings, 
        loading, 
        error, 
        updateUserSettings,
        refetch: () => fetchUserSettings(),
        isRealtimeConnected: isConnected
    };
};
